import angular from "angular";

angular
    .module('api-common')
    .factory('ClientVersionService', ClientVersionService);

const checkIntervalMilliseconds = 15 * 60 * 1000;
const minimumCheckGapMilliseconds = 60 * 1000;
const reloadGuardKey = 'clientVersionReloadTarget';
const bundlePattern = /(?:^|\/)(app\.[0-9a-f]+\.js)(?:[?#]|$)/;
const servedBundlePattern = /<script[^>]*\ssrc="([^"]*)"/g;

// Read while the bundle is still executing: the current script is, by definition, the code running.
const loadedFrom = typeof document !== 'undefined' && document.currentScript
    ? document.currentScript.getAttribute('src')
    : null;

ClientVersionService.$inject = ['$http', '$q', '$window'];

/**
 * Detects that a newer build has been deployed since this tab loaded, by comparing the bundle
 * this page is running with the one the served index.html names, and reloads onto it at a moment
 * that loses nothing.
 */
export function ClientVersionService($http, $q, $window, loadedFromSrc = loadedFrom) {
    const current = runningBundle();
    const enabled = !!current;
    let latest = undefined;
    let lastCheckAt = 0;
    let checkPromise = undefined;
    let started = false;

    return {
        check,
        isStale,
        reloadTo,
        start
    };

    function isStale() {
        if (!enabled || !latest) {
            return false;
        }
        return latest !== current;
    }

    /** Resolves to isStale() after refreshing it, at most once a minute. */
    function check() {
        if (!enabled) {
            return $q.resolve(false);
        }
        if (checkPromise) {
            return checkPromise;
        }
        if (Date.now() - lastCheckAt < minimumCheckGapMilliseconds) {
            return $q.resolve(isStale());
        }
        lastCheckAt = Date.now();
        checkPromise = $http.get('/', {
            cache: false,
            headers: {'Cache-Control': 'no-cache'},
            transformResponse: data => data
        }).then(function (response) {
            const served = servedBundle(response.data);
            if (served) {
                latest = served;
            }
            return isStale();
        }, function () {
            return isStale();
        }).finally(function () {
            checkPromise = undefined;
        });
        return checkPromise;
    }

    /**
     * Full page load of `url` (or the current page) when stale. Tries each newer version at most
     * once per tab, so a cached index.html that still names the old bundle cannot cause a loop.
     */
    function reloadTo(url) {
        // Without storage the guard cannot hold, so it is safer not to reload at all.
        if (!isStale() || readGuard() === latest || !writeGuard(latest)) {
            return false;
        }
        if (url && url !== $window.location.href) {
            $window.history.pushState(null, '', url);
        }
        $window.location.reload();
        return true;
    }

    /** Re-checks on returning to the tab and periodically while visible. */
    function start(onStale) {
        if (!enabled || started) {
            return;
        }
        started = true;
        const checkAndNotify = function () {
            if ($window.document.hidden) {
                return;
            }
            check().then(function (stale) {
                if (stale && onStale) {
                    onStale();
                }
            });
        };
        $window.document.addEventListener('visibilitychange', checkAndNotify);
        $window.setInterval(checkAndNotify, checkIntervalMilliseconds);
    }

    function runningBundle() {
        const loaded = bundlePattern.exec(loadedFromSrc || '');
        if (loaded) {
            return loaded[1];
        }
        const scripts = $window.document.getElementsByTagName('script');
        for (let i = 0; i < scripts.length; i++) {
            const match = bundlePattern.exec(scripts[i].getAttribute('src') || '');
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    function servedBundle(html) {
        const text = String(html || '');
        let script;
        servedBundlePattern.lastIndex = 0;
        while ((script = servedBundlePattern.exec(text)) !== null) {
            const match = bundlePattern.exec(script[1]);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    function readGuard() {
        try {
            return $window.sessionStorage.getItem(reloadGuardKey);
        } catch (error) {
            return null;
        }
    }

    function writeGuard(value) {
        try {
            $window.sessionStorage.setItem(reloadGuardKey, value);
            return $window.sessionStorage.getItem(reloadGuardKey) === value;
        } catch (error) {
            return false;
        }
    }
}
